import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, Zap, History } from "lucide-react";
import { useFreshContent } from "@/hooks/useFreshContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SourceLine = ({ source, url, testid }) => {
  if (!source) return null;
  const cls = "font-mono text-[8px] uppercase tracking-wide text-gf-muted/70 mt-2 inline-block";
  return url ? (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className={`${cls} underline decoration-gf-primary/40 underline-offset-2 hover:text-gf-primary transition-colors`}>Source: {source}</a>
  ) : (
    <p className={cls} data-testid={testid}>Source: {source}</p>
  );
};

const SignalArchive = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const toggle = () => {
    setOpen(!open);
    if (!open && items === null) {
      fetch(`${API}/public/market-signals/archive`).then((r) => r.json())
        .then((d) => setItems(d.items || [])).catch(() => setItems([]));
    }
  };
  return (
    <div className="mt-5">
      <button onClick={toggle} data-testid="signals-archive-toggle"
        className="font-mono text-[9px] uppercase tracking-wide text-gf-muted hover:text-gf-primary flex items-center gap-1.5 transition-colors">
        <History className="w-3 h-3" /> Past signals {open ? "▾" : "▸"}</button>
      {open && (
        items === null ? <p className="font-body text-[10px] text-gf-muted mt-2">Loading…</p> : (
          !items.length ? <p className="font-body text-[10px] text-gf-muted mt-2">The dated signal log starts with the next refresh.</p> : (
            <div className="space-y-2 mt-3 max-h-64 overflow-y-auto pr-2" data-testid="signals-archive-list">
              {items.map((a) => (
                <div key={a.id} className="border-l border-white/10 pl-3" data-testid={`signals-archive-${a.date}`}>
                  <p className="font-mono text-[8px] uppercase text-gf-primary/80">{a.date}</p>
                  {(a.insights || []).map((s, i) => (
                    <p key={i} className="font-body text-[10px] text-gf-muted mt-0.5">
                      <span className="font-bold text-gf-text">{s.stat}</span> — {s.label}{s.source ? ` (${s.source})` : ""}</p>))}
                </div>))}
            </div>)))}
    </div>
  );
};

export const FreshToday = () => {
  const c = useFreshContent("home");
  if (!c || (!c.insights?.length && !c.today?.length)) return null;
  return (
    <section className="relative border-b border-white/5 bg-gf-surface/40 py-14" data-testid="fresh-today-strip">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-10">
          {c.insights?.length > 0 && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[3px] text-gf-primary flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5" /> Market signals — refreshed through the day</p>
              <div className="grid sm:grid-cols-3 gap-3 mt-5">
                {c.insights.slice(0, 3).map((s, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="border border-white/10 bg-gf-bg p-4" data-testid={`fresh-insight-${i}`}>
                    <p className="font-heading font-black text-gf-primary text-2xl">{s.stat}</p>
                    <p className="font-body text-[11px] font-bold text-gf-text mt-1.5">{s.label}</p>
                    <p className="font-body text-[10px] text-gf-muted mt-1 leading-relaxed">{s.note}</p>
                    {s.source && <SourceLine source={s.source} url={s.source_url} testid={`fresh-insight-source-${i}`} />}
                  </motion.div>))}
              </div>
              <SignalArchive />
              <Link to="/market-prices" data-testid="home-market-prices-cta"
                className="mt-5 block border border-gf-primary/40 bg-gf-primary/5 p-4 hover:bg-gf-primary/10 transition-colors group">
                <p className="font-mono text-[9px] uppercase tracking-wide text-gf-primary flex items-center gap-1.5">
                  <Zap className="w-3 h-3" /> Live exchange price graphs</p>
                <p className="font-body text-sm font-bold text-gf-text mt-1">DAM to TAM — every 15-minute block, eight market segments, since Jan 2026.</p>
                <p className="font-body text-xs text-gf-muted mt-1">Optimise your energy management strategy — connect with us.
                  <span className="text-gf-primary group-hover:underline ml-1">View the graphs →</span></p>
              </Link>
            </div>)}
          {c.today?.length > 0 && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[3px] text-gf-primary flex items-center gap-2">
                <Zap className="w-3.5 h-3.5" /> Today at Growthifye</p>
              <div className="mt-5 space-y-3">
                {c.today.slice(0, 3).map((t, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: 14 }} whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="border-l-2 border-gf-primary/60 pl-4" data-testid={`fresh-today-${i}`}>
                    <p className="font-body text-sm font-bold text-gf-text">{t.title}</p>
                    <p className="font-body text-xs text-gf-muted mt-0.5 leading-relaxed">{t.body}</p>
                    {t.source && <SourceLine source={t.source} url={t.source_url} testid={`fresh-today-source-${i}`} />}
                  </motion.div>))}
              </div>
            </div>)}
        </div>
      </div>
    </section>
  );
};
